import * as React from 'react';

import { FlowOutputSelectorAction } from '~src/flow/components/FlowOutputSelectorContent';
import { FlowScheduleSelectorAction } from '~src/flow/components/FlowScheduleSelector';
import { assertUnreachable } from '~src/ts/helpers/TypescriptHelpers';

import { FlowLinkedTaskOptionsAction } from '../components/FlowLinkedTaskOptions';
import { LinkedTaskFlowSelectorAction } from '../components/LinkedTaskItemSelectorContent';
import { LinkedTaskAddItemBeforeAfterAction } from '../components/LinkedTaskMoveItemButton';
import { LinkedTaskToggleExpandAction } from '../components/LinkedTasksDialogContent';
import { displayMaximumLinkedTaskMessage,insertInArray,LinkedTaskFlowInitialState, linkedTaskInitialState, maximumLinkedTasks,updateInArray } from '../utils/LinkedTaskHelpers';
import { LinkedTaskDialogAddActions, LinkedTaskDialogState, LinkedTaskFlowState, LinkedTaskItemStateType, LinkedTaskOutputSelectionState, LinkedTaskPositionActions } from './LinkedTaskStateTypes';

export type LinkedTaskDialogAction =
  FlowScheduleSelectorAction |
  LinkedTaskDialogItemStateAction |
  { type: 'setState', value: LinkedTaskDialogState };

type LinkedTaskDialogItemStateAction =
  LinkedTaskToggleExpandAction |
  FlowLinkedTaskOptionsAction |
  LinkedTaskFlowSelectorAction |
  LinkedTaskDialogAddActions |
  LinkedTaskPositionActions |
  FlowOutputSelectorAction |
  LinkedTaskAddItemBeforeAfterAction;

function itemsReducer(items: LinkedTaskItemStateType[], action: LinkedTaskDialogItemStateAction): LinkedTaskItemStateType[] {
  let flow: LinkedTaskFlowState;
  let updatedItem;
  switch(action.type) {
    // Adding flows
    case 'addFlow':
      if (items.length >= maximumLinkedTasks) {
        displayMaximumLinkedTaskMessage();
        return items;
      }
      items.splice(action.payload.position !== undefined ? action.payload.position : items.length, 0, action.payload.value);
      return [...items];
    case 'addFlows':
      if (items.length + action.payload.value.length >= maximumLinkedTasks) {
        displayMaximumLinkedTaskMessage();
        return items;
      }
      items.splice(action.payload.position !== undefined ? action.payload.position : items.length, action.payload.overwrite ? 1 : 0, ...action.payload.value);
      return [...items];
    // Adding a new blank item
    case 'addNextTask':
      if (items.length >= maximumLinkedTasks) {
        displayMaximumLinkedTaskMessage();
        return items;
      }
      return [...items, {}];
    // Moving items
    case 'moveItem':
      const item = items.splice(action.payload.index, 1)[0];
      switch(action.payload.position) {
        case 'up':
          const index = action.payload.index > 0 ? action.payload.index - 1 : 0;
          items.splice(index, 0, item);
          break;
        case 'down':
          items.splice(action.payload.index + 1, 0, item);
          break;
        case 'beginning':
          items.unshift(item);
          break;
        case 'end':
          items.push(item);
          break;
      }
      return [...items];
    case 'addBeforeAfter':
      if (items.length >= maximumLinkedTasks) {
        displayMaximumLinkedTaskMessage();
        return items;
      }
      return insertInArray(action.payload.index, action.payload.position, {}, items);
    // Flow Options
    case 'setDqwEnabled':
      updatedItem =  {...items[action.payload.index],
        dqwEnabled: action.payload.value
      };
      return updateInArray(action.payload.index, updatedItem, items);
    case 'setStopRemainingTasks':
      updatedItem = {...items[action.payload.index],
        stopRemainingTasks: action.payload.value
      };
      return updateInArray(action.payload.index, updatedItem, items);
    case 'deleteTaskEntry':
      items.splice(action.value, 1);
      return [...items];
    // DQW Warning
    case 'setEnableTrigger':
      // This is the same action as setDqwEnabled
      updatedItem =  {...items[action.payload.index],
        dqwEnabled: action.payload.value
      };
      return updateInArray(action.payload.index, updatedItem, items);
    case 'setIsSevere':
      flow = items[action.payload.index] as LinkedTaskFlowState;
      updatedItem = {...flow,
        dqw: {...flow.dqw,
          isSevere: action.payload.value
        }
      };
      return updateInArray(action.payload.index, updatedItem, items);
    case 'setMessage':
      flow = items[action.payload.index] as LinkedTaskFlowState;
      updatedItem = {...flow,
        dqw: {...flow.dqw,
          message: action.payload.value
        }
      };
      return updateInArray(action.payload.index, updatedItem, items);
    case 'setFlow':
      updatedItem = LinkedTaskFlowInitialState(action.payload.flowId, action.payload.flowName, (items[action.payload.index] as LinkedTaskFlowState).linkedTaskStepId);
      return updateInArray(action.payload.index, updatedItem, items);
    case 'toggleExpandItem':
      flow = items[action.payload.index] as LinkedTaskFlowState;
      updatedItem = {...flow,
        isExpanded: !flow.isExpanded
      };
      return updateInArray(action.payload.index, updatedItem, items);
    default:
      flow = items[action.payload.index] as LinkedTaskFlowState;
      updatedItem = {
        ...flow,
        outputSelection: outputSelectionReducer(flow.outputSelection, action),
      };
      return updateInArray(action.payload.index, updatedItem, items);
  }
}

function outputSelectionReducer(state: LinkedTaskOutputSelectionState, action: FlowOutputSelectorAction): LinkedTaskOutputSelectionState {
  switch(action.type) {
    case 'selectOutput':
      return {...state,
        selectedOutputs: {
          ...state.selectedOutputs,
          [action.payload.value]: state.selectedOutputs[action.payload.value] ? !state.selectedOutputs[action.payload.value] : true,
        }
      };
    case 'selectOutputs':
      return {...state,
        selectedOutputs: action.payload.value,
      };
    case 'setIsIncremental':
      return {...state,
        isIncrementalMap: {
          ...state.isIncrementalMap,
          [action.payload.outputId]: action.payload.value,
        }
      };
    case 'setFutureOutputsIncluded':
      return {...state,
        futureOutputsIncluded: action.payload.value,
      };
    case 'setAllOutputsIncremental':
      return {...state,
        runAllOption: action.payload.value,
      };
    case 'setSelectAllChecked':
      return  {...state,
        selectAllChecked: action.payload.value,
      };
    default:
      assertUnreachable(action);
      return state;
  }
}

function reducer(state: LinkedTaskDialogState, action: LinkedTaskDialogAction): LinkedTaskDialogState {
  switch (action.type) {
    // Setting full state (editing existing linked task)
    case 'setState':
      return action.value;
    // Schedule Selector
    case 'setSelectedSchedule':
      return {
        ...state,
        selectedSchedule: action.value
      };
    default:
      return {
        ...state,
        items: itemsReducer(state.items, action)
      };
  }
}

export function useLinkedTaskDialogStateHook(): [LinkedTaskDialogState, React.Dispatch<LinkedTaskDialogAction>] {
  const [state, dispatch] = React.useReducer(reducer, linkedTaskInitialState);
  return [state, dispatch];
}
